/**
 * DepositListPage - Página para listar y gestionar depósitos
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Button,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from '../config/dayjs';
import AddIcon from '@mui/icons-material/Add';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDeposito, useNotification } from '../context';
import { catalogoService } from '../services';
import PageHeader from '../components/common/PageHeader';
import DataTable from '../components/common/DataTable';
import LoadingSpinner from '../components/common/LoadingSpinner';

const DepositListPage = () => {
  const navigate = useNavigate();
  const { depositos, fetchDepositos, downloadDepositPDF, deleteDeposito, loading } = useDeposito();
  const { showSuccess, showError } = useNotification();

  // Filtros
  const [filters, setFilters] = useState({
    fechaDesde: null,
    fechaHasta: null,
    cuentaBancariaId: '',
    bancoId: '',
    usuarioId: '',
  });

  // Estado del diálogo de confirmación
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    deposito: null,
    deleting: false,
  });

  // Catálogos
  const [bancos, setBancos] = useState([]);
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loadingCatalogos, setLoadingCatalogos] = useState(true);

  // Cargar catálogos al montar
  useEffect(() => {
    loadCatalogos();
  }, []);

  // Cargar depósitos al montar y cuando cambien los filtros
  useEffect(() => {
    loadDepositos();
  }, [filters]);

  const loadCatalogos = async () => {
    try {
      setLoadingCatalogos(true);
      const [bancosData, cuentasData, usuariosData] = await Promise.all([
        catalogoService.getBancos(),
        catalogoService.getCuentasBancarias(),
        catalogoService.getUsuarios(),
      ]);
      setBancos(bancosData);
      setCuentasBancarias(cuentasData);
      setUsuarios(usuariosData);
    } catch (error) {
      showError('Error al cargar catálogos');
    } finally {
      setLoadingCatalogos(false);
    }
  };

  const loadDepositos = async () => {
    const filterParams = {};

    if (filters.fechaDesde) {
      filterParams.fecha_inicio = filters.fechaDesde.format('YYYY-MM-DD');
    }
    if (filters.fechaHasta) {
      filterParams.fecha_fin = filters.fechaHasta.format('YYYY-MM-DD');
    }
    if (filters.cuentaBancariaId) {
      filterParams.cuenta_bancaria_id = filters.cuentaBancariaId;
    }
    if (filters.bancoId) {
      filterParams.banco_destino_id = filters.bancoId;
    }
    if (filters.usuarioId) {
      filterParams.usuario_id = filters.usuarioId;
    }

    await fetchDepositos(filterParams);
  };

  const handleClearFilters = () => {
    setFilters({
      fechaDesde: null,
      fechaHasta: null,
      cuentaBancariaId: '',
      bancoId: '',
      usuarioId: '',
    });
  };

  const handleDownloadPDF = async (deposito) => {
    try {
      await downloadDepositPDF(deposito.id, `deposito-${deposito.numero_boleta || deposito.id}.pdf`);
      showSuccess('PDF descargado exitosamente');
    } catch (error) {
      showError('Error al descargar PDF');
    }
  };

  const handleViewDeposit = (deposito) => {
    navigate(`/depositos/${deposito.id}`);
  };

  const handleRowClick = (deposito) => {
    navigate(`/depositos/${deposito.id}`);
  };

  const handleOpenDeleteDialog = (deposito) => {
    setDeleteDialog({
      open: true,
      deposito,
      deleting: false,
    });
  };

  const handleCloseDeleteDialog = () => {
    if (!deleteDialog.deleting) {
      setDeleteDialog({
        open: false,
        deposito: null,
        deleting: false,
      });
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialog.deposito) return;

    try {
      setDeleteDialog((prev) => ({ ...prev, deleting: true }));

      await deleteDeposito(deleteDialog.deposito.id);

      showSuccess('Depósito eliminado exitosamente. Los cheques han vuelto al estado pendiente.');

      handleCloseDeleteDialog();

      // Recargar depósitos
      loadDepositos();
    } catch (error) {
      console.error('Error al eliminar depósito:', error);
      const errorMessage = error.response?.data?.error || 'Error al eliminar el depósito';
      showError(errorMessage);
      setDeleteDialog((prev) => ({ ...prev, deleting: false }));
    }
  };

  // Formatear moneda
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
    }).format(value);
  };

  // Formatear fecha
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-AR');
  };

  // Definición de columnas
  const columns = [
    {
      field: 'numero_boleta',
      headerName: 'Nº Comprobante',
      render: (value, row) => value || `#${row.id}`,
    },
    {
      field: 'fecha_deposito',
      headerName: 'Fecha',
      render: (value) => formatDate(value),
    },
    {
      field: 'cuenta_bancaria',
      headerName: 'Cuenta Bancaria',
      render: (value, row) => {
        if (row.cuenta_bancaria) {
          const cuenta = row.cuenta_bancaria;
          return `${cuenta.banco?.nombre || 'Banco'} - ${cuenta.numero_cuenta}`;
        }
        return '-';
      },
    },
    {
      field: 'cheques',
      headerName: '# Cheques',
      align: 'center',
      render: (value) => (
        <Chip label={value?.length || 0} color="primary" size="small" />
      ),
    },
    {
      field: 'monto_total',
      headerName: 'Monto Total',
      align: 'right',
      render: (value) => formatCurrency(parseFloat(value) || 0),
    },
    {
      field: 'usuario',
      headerName: 'Usuario',
      render: (value, row) => row.usuario?.nombre || '-',
    },
  ];

  if (loadingCatalogos) {
    return <LoadingSpinner message="Cargando información..." />;
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <PageHeader
        title="Depósitos"
        subtitle="Gestione los depósitos de cheques"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Depósitos' },
        ]}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/depositos/nuevo')}
          >
            Nuevo Depósito
          </Button>
        }
      />

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
              <DatePicker
                label="Fecha Desde"
                value={filters.fechaDesde}
                onChange={(newValue) => setFilters({ ...filters, fechaDesde: newValue })}
                slotProps={{
                  textField: { size: 'small', fullWidth: true },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
              <DatePicker
                label="Fecha Hasta"
                value={filters.fechaHasta}
                onChange={(newValue) => setFilters({ ...filters, fechaHasta: newValue })}
                slotProps={{
                  textField: { size: 'small', fullWidth: true },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Cuenta Bancaria</InputLabel>
              <Select
                value={filters.cuentaBancariaId}
                label="Cuenta Bancaria"
                onChange={(e) =>
                  setFilters({ ...filters, cuentaBancariaId: e.target.value })
                }
              >
                <MenuItem value="">Todas</MenuItem>
                {cuentasBancarias.map((cuenta) => (
                  <MenuItem key={cuenta.id} value={cuenta.id}>
                    {cuenta.banco?.nombre || 'Banco'} - {cuenta.numero_cuenta}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Banco</InputLabel>
              <Select
                value={filters.bancoId}
                label="Banco"
                onChange={(e) => setFilters({ ...filters, bancoId: e.target.value })}
              >
                <MenuItem value="">Todos</MenuItem>
                {bancos.map((banco) => (
                  <MenuItem key={banco.id} value={banco.id}>
                    {banco.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Usuario</InputLabel>
              <Select
                value={filters.usuarioId}
                label="Usuario"
                onChange={(e) => setFilters({ ...filters, usuarioId: e.target.value })}
              >
                <MenuItem value="">Todos</MenuItem>
                {usuarios.map((usuario) => (
                  <MenuItem key={usuario.id} value={usuario.id}>
                    {usuario.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} display="flex" justifyContent="flex-end">
            <Button onClick={handleClearFilters} variant="outlined" size="small">
              Limpiar Filtros
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabla de depósitos */}
      {loading ? (
        <LoadingSpinner message="Cargando depósitos..." />
      ) : (
        <DataTable
          columns={columns}
          data={Array.isArray(depositos) ? depositos : []}
          searchPlaceholder="Buscar depósitos..."
          emptyMessage="No se encontraron depósitos"
          renderCustomActions={(deposito) => (
            <>
              <Tooltip title="Ver detalle">
                <IconButton
                  size="small"
                  onClick={() => handleViewDeposit(deposito)}
                  color="primary"
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Descargar PDF">
                <IconButton
                  size="small"
                  onClick={() => handleDownloadPDF(deposito)}
                  color="secondary"
                >
                  <PictureAsPdfIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Eliminar depósito">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenDeleteDialog(deposito);
                  }}
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        />
      )}

      {/* Diálogo de confirmación de eliminación */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro que desea eliminar este depósito?
          </DialogContentText>
          {deleteDialog.deposito && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Comprobante:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {deleteDialog.deposito.numero_boleta || `#${deleteDialog.deposito.id}`}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Fecha:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatDate(deleteDialog.deposito.fecha_deposito)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Cantidad de cheques:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {deleteDialog.deposito.cheques?.length || 0}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Monto total:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium" color="primary">
                    {formatCurrency(parseFloat(deleteDialog.deposito.monto_total) || 0)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
          <DialogContentText sx={{ mt: 2, color: 'warning.main' }}>
            Los cheques asociados volverán al estado "pendiente" y podrán ser depositados nuevamente.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDeleteDialog}
            disabled={deleteDialog.deleting}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteDialog.deleting}
          >
            {deleteDialog.deleting ? 'Eliminando...' : 'Eliminar Depósito'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DepositListPage;
